<template>
  <div class="vacation-response-day-presence" data-testid="vacation-day-presence-options">
    <div class="vacation-presence-radio-group" role="radiogroup" :aria-label="radioGroupName">
      <div>
        <label class="custom-control custom-radio" data-testid="vacation-come-checkbox">
          <input
            v-model="isComingModel"
            type="radio"
            class="custom-control-input"
            :name="radioGroupName"
            :aria-label="comingLabel"
            :value="true"
            :disabled="isComingRadioDisabled"
          />
          <span class="custom-control-label">{{ comingLabel }}</span>
        </label>
        <div
          v-if="vacationDay.isComing && vacationDay.useTimes"
          class="checkin-time"
          data-testid="vacation-checkin-times"
        >
          <div class="checkin-time-field" data-testid="vacation-entry-time">
            <span>{{ entryTimeLabel }}</span>
            <VacationIntervalsFragmentTimepicker
              v-model="entryTimeModel"
              class="time"
              :class="timepickerErrorClass"
              :timepicker-value="entryTimeModel"
              :disabled="isReadonly || vacationDay.isDisabled"
              :start="vacationDay.getStartTime()"
              :end="vacationDay.getEndTime()"
            />
          </div>
          <div class="checkin-time-field" data-testid="vacation-exit-time">
            <span>{{ exitTimeLabel }}</span>
            <VacationIntervalsFragmentTimepicker
              v-model="exitTimeModel"
              class="time"
              :class="timepickerErrorClass"
              :timepicker-value="exitTimeModel"
              :disabled="isReadonly || vacationDay.isDisabled"
              :start="vacationDay.getStartTime()"
              :end="vacationDay.getEndTime()"
            />
          </div>
        </div>
        <div class="alert-wrapper">
          <b-alert variant="danger" :show="vacationDay.getShowIsWithinOpeningHourError()">
            {{ $getText('RESPONSE_VACATION_TIMES_OUTSIDE_OPENING_HOURS') }}
          </b-alert>
          <b-alert variant="danger" :show="vacationDay.getShowHasTimesError()">
            {{ $getText('RESPONSE_VACATION_COMING_TIME_INVALID') }}
          </b-alert>
          <b-alert variant="danger" :show="vacationDay.getShowIsValidTimesError()">
            {{ $getText('PRESENCE_ALERT_INVALID_START_AND_END_TIME') }}
          </b-alert>
        </div>
      </div>

      <label class="custom-control custom-radio" data-testid="vacation-not-come-checkbox">
        <input
          v-model="isComingModel"
          type="radio"
          class="custom-control-input"
          :name="radioGroupName"
          :aria-label="notComingLabel"
          :value="false"
          :disabled="isNotComingRadioDisabled"
        />
        <span class="custom-control-label">{{ notComingLabel }}</span>
      </label>
    </div>
  </div>
</template>

<script lang="js">
import VacationIntervalsFragmentTimepicker from './VacationIntervalsFragmentTimepicker';
import {mapGetters} from "vuex";
import {types} from "../../../src/store/types/types";

export default {
  name: 'VacationResponseDayPresence',
  components: {
    VacationIntervalsFragmentTimepicker,
  },
  props: {
    vacationDay: {
      type: Object,
      required: true,
    },
    isReadonly: {
      type: Boolean,
      default: false,
    },
    comingLabel: {
      type: String,
      required: true,
    },
    notComingLabel: {
      type: String,
      required: true,
    },
    entryTimeLabel: {
      type: String,
      required: true,
    },
    exitTimeLabel: {
      type: String,
      required: true,
    },
  },
  computed: {
    ...mapGetters({
      isMobile: types.GET_IS_MOBILE
    }),
    radioGroupName() {
      return `vacation-presence-${this.vacationDay.date}`;
    },
    isDayLocked() {
      return this.isReadonly || this.vacationDay.isDisabled;
    },
    isComingRadioDisabled() {
      return this.isDayLocked && this.vacationDay.isComing !== true;
    },
    isNotComingRadioDisabled() {
      return this.isDayLocked && this.vacationDay.isComing !== false;
    },
    isComingModel: {
      get() {
        return this.vacationDay.isComing;
      },
      set(value) {
        this.setPresence(value);
      },
    },
    entryTimeModel: {
      get() {
        return this.vacationDay.entryTime || '';
      },
      set(value) {
        this.setEntryTime(value);
      },
    },
    exitTimeModel: {
      get() {
        return this.vacationDay.exitTime || '';
      },
      set(value) {
        this.setExitTime(value);
      },
    },
    timepickerErrorClass() {
      return {
        'red-border':
          this.vacationDay.getShowIsWithinOpeningHourError() ||
          this.vacationDay.getShowHasTimesError() ||
          this.vacationDay.getShowIsValidTimesError(),
      };
    },
  },
  methods: {
    setPresence(value) {
      if (value === this.vacationDay.isComing) {
        return;
      }
      this.vacationDay.setIsComing(value);
      if (value === false) {
        this.vacationDay.setEntryTime('');
        this.vacationDay.setExitTime('');
      }
      this.$emit('changed');
    },
    setEntryTime(value) {
      this.setTime(this.vacationDay.entryTime, value, nextValue => this.vacationDay.setEntryTime(nextValue));
    },
    setExitTime(value) {
      this.setTime(this.vacationDay.exitTime, value, nextValue => this.vacationDay.setExitTime(nextValue));
    },
    setTime(currentValue, value, applyValue) {
      const nextValue = typeof value === 'string' ? value : '';
      if (nextValue === (currentValue || '')) {
        return;
      }
      applyValue(nextValue);
      this.vacationDay.syncToInterval();
      this.vacationDay.isValid();
      this.$emit('changed');
    },
  },
};
</script>

<style lang="scss" scoped>
@use '../../../shared/assets/scss/core/breakpoints' as *;

.vue-drawer > .drawer .drawer-content label.custom-control:not(.custom-control-label) {
  margin-bottom: 0;
  margin-top: 0;
  margin-left: 0.75rem;
  @include breakpoint-lg-down() {
    margin-left: 0.25rem;
  }
}

.vacation-presence-radio-group {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
  @include breakpoint-lg-down() {
    grid-template-columns: 1fr;
  }
}

.checkin-time {
  margin-left: 2.375rem;
  margin-top: 0.5rem;
  display: flex;
  gap: 0.5rem;
  @include breakpoint-lg-down() {
    margin-left: 1.875rem;
  }
}

.checkin-time-field {
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-bottom: 0.25rem;
}

.alert-wrapper {
  margin-left: 2.25rem;
}
</style>
